<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">Attendance Report</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="fetchAttendanceHistory" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="report-content">
      <div class="report-container">
        
        <!-- Month Selector Bento Card -->
        <div class="month-selector-card">
          <div class="selector-header">
            <ion-icon :icon="calendarOutline" class="selector-icon"></ion-icon>
            <h3>Choose Report Period</h3>
          </div>
          <div class="form-group-custom" style="padding-top: 10px;">
            <ion-item class="form-item-custom" lines="none" style="display:flex; justify-content:space-between; align-items:center;">
              <ion-label class="custom-label">Select Month & Year</ion-label>
              <ion-datetime
                display-format="MMMM YYYY"
                picker-format="MMMM YYYY"
                placeholder="Select Month"
                v-model="selectedMonth"
                @ionChange="fetchAttendanceHistory"
                mode="ios"
              ></ion-datetime>
            </ion-item>
          </div>
        </div>

        <!-- Dynamic Summary Stats Cards -->
        <div v-if="!loading && history.length > 0" class="stats-grid">
          <div class="stat-card present">
            <span class="stat-num">{{ stats.present }}</span>
            <span class="stat-label">Present</span>
          </div>
          <div class="stat-card late">
            <span class="stat-num">{{ stats.late }}</span>
            <span class="stat-label">Late Logs</span>
          </div>
          <div class="stat-card absent">
            <span class="stat-num">{{ stats.absent }}</span>
            <span class="stat-label">Absences</span>
          </div>
          <div class="stat-card other">
            <span class="stat-num">{{ stats.leave + stats.holiday + stats.offDay }}</span>
            <span class="stat-label">Holidays/Leaves</span>
          </div>
        </div>

        <!-- Attendance List -->
        <div v-if="loading" class="loading-state">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <span>Compiling attendance logs...</span>
        </div>

        <div v-else-if="history.length > 0" class="history-list">
          <div 
            v-for="day in history" 
            :key="day.date" 
            class="history-card"
            :class="day.status.toLowerCase().replace(' ', '-')"
          >
            <div class="card-glow"></div>

            <div class="card-header-row">
              <div class="day-info">
                <div class="day-icon-wrapper">
                  <ion-icon :icon="getStatusIcon(day.status)" class="day-icon"></ion-icon>
                </div>
                <div>
                  <h3 class="day-title">{{ formatDate(day.date) }}</h3>
                  <span class="day-subtitle">{{ day.day }}</span>
                </div>
              </div>
              
              <div class="status-badge" :class="day.status.toLowerCase().replace(' ', '-')">
                <span class="status-dot"></span>
                <span>{{ day.status }}</span>
              </div>
            </div>

            <div class="card-body" v-if="day.check_in || day.check_out || day.notes">
              <!-- Check-In/Check-Out Bento Box -->
              <div class="time-details" v-if="day.check_in || day.check_out">
                <div class="time-col" v-if="day.check_in">
                  <div class="time-header">
                    <ion-icon :icon="logInOutline"></ion-icon>
                    <span>CHECK IN</span>
                  </div>
                  <span class="time-val">{{ day.check_in }}</span>
                </div>
                <div class="time-col" v-if="day.check_out">
                  <div class="time-header">
                    <ion-icon :icon="logOutOutline"></ion-icon>
                    <span>CHECK OUT</span>
                  </div>
                  <span class="time-val">{{ day.check_out }}</span>
                </div>
              </div>

              <!-- Notes/Exceptions Box -->
              <div class="note-box" v-if="day.notes">
                <ion-icon :icon="documentTextOutline" class="note-icon"></ion-icon>
                <span class="note-text">{{ day.notes }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon-wrapper">
            <ion-icon :icon="alertCircleOutline" class="empty-icon"></ion-icon>
          </div>
          <h2>No Data Found</h2>
          <p>We couldn't retrieve any attendance records for the selected month of {{ formatMonthTitle(selectedMonth) }}.</p>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButton, 
  IonIcon, 
  IonSpinner,
  IonItem,
  IonLabel,
  IonInput,
  IonDatetime
} from '@ionic/vue';
import { 
  refreshOutline, 
  calendarOutline, 
  checkmarkCircleOutline, 
  closeCircleOutline, 
  alertCircleOutline, 
  airplaneOutline, 
  sunnyOutline, 
  briefcaseOutline, 
  timeOutline, 
  documentTextOutline, 
  logInOutline, 
  logOutOutline,
  businessOutline
} from 'ionicons/icons';

export default {
  name: 'AttendanceReport',
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonIcon,
    IonSpinner,
    IonItem,
    IonLabel,
    IonInput,
    IonDatetime
  },
  data() {
    return {
      selectedMonth: new Date().toISOString().substring(0, 7), // YYYY-MM format
      history: [],
      loading: true,
      // Icons
      refreshOutline,
      calendarOutline,
      logInOutline,
      logOutOutline,
      documentTextOutline,
      alertCircleOutline
    }
  },
  created() {
    this.fetchAttendanceHistory();
  },
  computed: {
    stats() {
      let present = 0;
      let late = 0;
      let fieldVisit = 0;
      let absent = 0;
      let leave = 0;
      let holiday = 0;
      let offDay = 0;

      this.history.forEach(day => {
        if (!day.status) return;
        const s = day.status.toLowerCase();
        if (s.includes('present')) present++;
        else if (s.includes('late')) late++;
        else if (s.includes('field')) fieldVisit++;
        else if (s.includes('absent')) absent++;
        else if (s.includes('leave')) leave++;
        else if (s.includes('holiday')) holiday++;
        else if (s.includes('off')) offDay++;
      });

      return {
        present: present + late + fieldVisit,
        late: late,
        fieldVisit: fieldVisit,
        absent: absent,
        leave: leave,
        holiday: holiday,
        offDay: offDay
      };
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },
    formatMonthTitle(monthStr) {
      if (!monthStr) return '';
      try {
        const parts = monthStr.split('-');
        const date = new Date(parts[0], parseInt(parts[1]) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      } catch (e) {
        return monthStr;
      }
    },
    getStatusIcon(status) {
      if (!status) return alertCircleOutline;
      const s = status.toLowerCase();
      if (s.includes('present')) return checkmarkCircleOutline;
      if (s.includes('late')) return timeOutline;
      if (s.includes('field')) return businessOutline;
      if (s.includes('leave')) return airplaneOutline;
      if (s.includes('holiday')) return sunnyOutline;
      if (s.includes('off')) return briefcaseOutline;
      return closeCircleOutline;
    },
    fetchAttendanceHistory() {
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
        month: this.selectedMonth
      };

      this.$http.post('hr/myAttendanceHistory', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.history = response.data.history;
          }
          this.loading = false;
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.report-content {
  --background: #f4f6fc;
}

.report-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.refresh-btn {
  --color: #fff;
}

/* Month Selector Bento Card */
.month-selector-card {
  background: #fff;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.01);
  margin-bottom: 16px;
}

.selector-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 10px;
}

.selector-icon {
  font-size: 1.25rem;
  color: #2563eb;
}

.selector-header h3 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #102a43;
}

.form-group-custom {
  margin-bottom: 4px;
}

.form-item-custom {
  --background: #f8fafc;
  --border-radius: 12px;
  --padding-start: 12px;
  --inner-padding-end: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.custom-label {
  color: #64748b;
  font-weight: 600;
  font-size: 0.82rem !important;
}

.custom-input {
  --color: #0f172a;
  font-weight: 700;
  font-size: 0.95rem;
}

/* Stats grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 18px;
}

.stat-card {
  background: #fff;
  padding: 10px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 15px rgba(16, 42, 67, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.01);
}

.stat-num {
  font-size: 1.15rem;
  font-weight: 800;
  color: #102a43;
}

.stat-label {
  font-size: 0.58rem;
  font-weight: 700;
  color: #829ab1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.stat-card.present .stat-num { color: #10b981; }
.stat-card.late .stat-num { color: #f59e0b; }
.stat-card.absent .stat-num { color: #ef4444; }
.stat-card.other .stat-num { color: #3b82f6; }

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #627d98;
}

/* History Card */
.history-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;
}

.history-card:active {
  transform: scale(0.99);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 5px;
}

/* Colors based on status */
.history-card.present .card-glow { background: #10b981; }
.history-card.late .card-glow { background: #f59e0b; }
.history-card.field-visit .card-glow { background: #06b6d4; }
.history-card.on-leave .card-glow { background: #3b82f6; }
.history-card.off-day .card-glow { background: #64748b; }
.history-card.holiday .card-glow { background: #eab308; }
.history-card.absent .card-glow { background: #ef4444; }

/* Header Row */
.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px 12px 20px;
}

.day-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.day-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
}

.history-card.present .day-icon-wrapper { background: #ecfdf5; .day-icon { color: #10b981; } }
.history-card.late .day-icon-wrapper { background: #fffbeb; .day-icon { color: #d97706; } }
.history-card.field-visit .day-icon-wrapper { background: #ecfeff; .day-icon { color: #0891b2; } }
.history-card.on-leave .day-icon-wrapper { background: #eff6ff; .day-icon { color: #2563eb; } }
.history-card.off-day .day-icon-wrapper { background: #f8fafc; .day-icon { color: #475569; } }
.history-card.holiday .day-icon-wrapper { background: #fef9c3; .day-icon { color: #ca8a04; } }
.history-card.absent .day-icon-wrapper { background: #fef2f2; .day-icon { color: #dc2626; } }

.day-icon {
  font-size: 1.15rem;
  color: #64748b;
}

.day-title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
}

.day-subtitle {
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 600;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-badge .status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}

.status-badge.present { background: #d1fae5; color: #065f46; .status-dot { background: #10b981; } }
.status-badge.late { background: #fef3c7; color: #92400e; .status-dot { background: #f59e0b; } }
.status-badge.field-visit { background: #cffafe; color: #0e7490; .status-dot { background: #06b6d4; } }
.status-badge.on-leave { background: #dbeafe; color: #1e40af; .status-dot { background: #3b82f6; } }
.status-badge.off-day { background: #f1f5f9; color: #334e68; .status-dot { background: #64748b; } }
.status-badge.holiday { background: #fef9c3; color: #854d0e; .status-dot { background: #eab308; } }
.status-badge.absent { background: #fee2e2; color: #991b1b; .status-dot { background: #ef4444; } }

/* Card Body */
.card-body {
  padding: 0 16px 14px 20px;
  border-top: 1px dashed #f1f5f9;
  padding-top: 10px;
}

/* Time Details row */
.time-details {
  display: flex;
  gap: 16px;
}

.time-col {
  flex: 1;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
}

.time-header {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  color: #94a3b8;
}

.time-header ion-icon {
  font-size: 0.75rem;
}

.time-val {
  font-size: 0.85rem;
  font-weight: 700;
  color: #334e68;
  margin-top: 1px;
}

/* Note box */
.note-box {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  background: #fefcbf;
  border: 1px solid #fef08a;
  border-radius: 8px;
  padding: 6px 10px;
}

.note-icon {
  font-size: 0.95rem;
  color: #ca8a04;
}

.note-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #854d0e;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  margin-top: 48px;
}

.empty-icon-wrapper {
  background: #fee2e2;
  padding: 20px;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon {
  font-size: 3rem;
  color: #ef4444;
}

.empty-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #102a43;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 0.88rem;
  color: #627d98;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
}
</style>
